import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { Layout } from "providers/Layout";
import { Link, useParams } from "react-router-dom";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { AlertCircle, Clock, Edit05, Link01 } from "untitledui-js-base";
import { ActivityLogDrawer } from "./components/ActivityLog";
import { useState } from "react";
import { Loader } from "shared/components/Loader";
import { InfoRow } from "./components/InfoRow";
import { RentReportingData, Subscriber } from "./types";
import moment from "moment";
import { formatPhone, formatToCurrency } from "shared/utils";

import { getErrorMessage } from "utils/error";
import { RentReportingTable } from "./components/RentReportingTable";
import { SubscriptionStatusBadge } from "./components/SubscriptionStatusBadge";
import { AccountStatusBadge } from "views/rent-reporting/components/AccountStatusBadge";
import {
  AccountStatusVariant,
  CaseStatusVariant
} from "views/rent-reporting/constants";
import { NotificationBanner } from "./components/NotificationBanner";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

export const SubscriptionDetails = () => {
  const { Unit, Property } = useUnitsPropertiesDictionary();
  const [showActivityLog, setShowActivityLog] = useState(false);
  const { renterId } = useParams();
  const { type } = useAuthContext();

  const {
    data: renter,
    isFetching,
    isError,
    error
  } = useQuery({
    queryKey: ["SUBSCRIBER_DETAIL", renterId],
    queryFn: () =>
      instance
        .get(`/portal/v1/customers/${renterId}`)
        .then(({ data }: { data: Subscriber }) => data)
  });

  const handleCopyVerificationLink = async () => {
    try {
      await navigator.clipboard.writeText(renter?.verification_link || "");

      toast.info("Link copied");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const {
    data,
    isLoading,
    isError: logsError
  } = useQuery({
    queryKey: ["AUDIT_LOGS", renterId],
    queryFn: () =>
      instance
        .get(`/portal/v1/customers/${renterId}/audit_logs`)
        .then(({ data }) => data),
    enabled: Boolean(renterId)
  });

  const pendingAccount =
    renter?.account_status === AccountStatusVariant.Pending;

  if (isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading subscriber details.
          </h1>
          <p className="text-base font-medium">{error.message}</p>
        </div>
      </Layout>
    );

  if (isFetching)
    return (
      <Layout
        headerClassName="py-0"
        header={
          <div className="border-b border-alpha-40 p-6">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Rent reporting",
                  href: "/rent-reporting"
                }
              ]}
            />
          </div>
        }
      >
        <div className="flex items-center justify-center">
          <Loader />
        </div>
      </Layout>
    );

  const displayStatus: AccountStatusVariant | undefined = (() => {
    if (!renter?.account_status) return undefined;

    if (renter.account_status === "pending") {
      if (
        renter.case_status === "pending_opt_in" ||
        renter.case_status === "pending_opt_out"
      )
        return renter.case_status as AccountStatusVariant;
      return AccountStatusVariant.PendingIDV;
    }

    return renter.account_status as AccountStatusVariant;
  })();

  return (
    <Layout
      headerClassName="py-0"
      header={
        <>
          <div className="border-b border-alpha-40 p-6">
            <BreadCrumbs
              crumbs={[
                {
                  title: "Rent reporting",
                  href: "/rent-reporting"
                },
                {
                  title: renter?.name || "--",
                  href: `/rent-reporting/${renterId}`
                }
              ]}
            />
          </div>
          <div className="flex items-center justify-between border-b border-alpha-100 px-6 py-3">
            <div>
              <h2 className="mb-1 text-sm text-content-400">
                Subscription Details
              </h2>
              <h2 className="text-xl font-bold text-content-1000">
                {renter?.name}
              </h2>
            </div>
            {(renter?.account_status !== AccountStatusVariant.Unsubscribed &&
              type.isManual) ||
            type.isPMS ? (
              <Link to={`/rent-reporting/${renterId}/edit`}>
                <Button
                  Icon={Edit05}
                  variant={ButtonVariant.BASE}
                  borderLess
                  className="stroke-primary-500 font-bold text-primary-500"
                  size={ButtonSize.SM}
                >
                  Edit Details
                </Button>
              </Link>
            ) : null}
            {renter?.account_status === AccountStatusVariant.Unsubscribed &&
            type.isManual ? (
              <div className="flex gap-1">
                {renter.can_resubscribe ? (
                  <Link to={`/rent-reporting/${renterId}/resubscribe`}>
                    <Button
                      variant={ButtonVariant.BASE}
                      borderLess
                      className="stroke-primary-500 font-bold text-primary-500"
                      size={ButtonSize.SM}
                    >
                      Resume subscription to current lease
                    </Button>
                  </Link>
                ) : null}

                <Link to={`/rent-reporting/${renterId}/new-subscription`}>
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    className="font-bold text-primary-500"
                    size={ButtonSize.SM}
                  >
                    Subscribe for new lease
                  </Button>
                </Link>
              </div>
            ) : null}
          </div>
          <div className="flex items-center justify-between p-6 border-b border-alpha-100 flex-wrap md:flex-nowrap">
            <div className="flex gap-4 items-center flex-wrap md:flex-nowrap">
              <div className="min-w-[120px] w-auto">
                <p className="mb-2 text-xs text-content-400">Subscription</p>
                {renter?.subscription_status ? (
                  <SubscriptionStatusBadge
                    status={renter.subscription_status}
                  />
                ) : (
                  "--"
                )}
              </div>
              <div className="min-w-[120px] w-auto">
                <p className="mb-2 text-xs text-content-400">Account Status</p>
                {displayStatus ? (
                  <AccountStatusBadge status={displayStatus} />
                ) : (
                  "--"
                )}
              </div>
              <div className="min-w-[120px] w-auto">
                <p className="mb-2 text-xs text-content-400">
                  Subscription Start
                </p>
                <p className="text-sm font-medium text-content-1000">
                  {renter?.subscription_start
                    ? moment(renter.subscription_start).format("MMM D, YYYY")
                    : "-"}
                </p>
              </div>
              <div className="min-w-[120px] w-auto">
                <p className="mb-2 text-xs text-content-400">
                  Subscription End
                </p>
                <p className="text-sm font-medium text-content-1000">
                  {renter?.subscription_end
                    ? moment(renter.subscription_end).format("MMM D, YYYY")
                    : "-"}
                </p>
              </div>
            </div>
            {type.isSDK ? null : (
              <Button
                Icon={Clock}
                size={ButtonSize.SM}
                onClick={() => {
                  setShowActivityLog(true);
                }}
              >
                Activity log
              </Button>
            )}
          </div>
        </>
      }
    >
      {pendingAccount && renter.verification_link ? (
        <NotificationBanner
          icon={<AlertCircle size="24" className="stroke-danger-600" />}
          title={`${renter.name} needs to complete manual identity verification`}
          text={`${renter.name} will receive an email with instructions to
        complete manual verification. Alternatively, you can send the verification link directly to
        the renter.`}
          button={
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={Link01}
              onClick={handleCopyVerificationLink}
            >
              Copy Verification Link
            </Button>
          }
        />
      ) : null}
      {pendingAccount &&
      renter.case_status === CaseStatusVariant.PendingOptIn &&
      (renter.remaining_opt_in_days ?? -1) >= 0 ? (
        <NotificationBanner
          icon={<Clock size="24" className="stroke-content-100" />}
          title={`${renter.name} has ${renter.remaining_opt_in_days} days to opt in`}
          text={`${renter.name} has been notified via email and has ${renter.remaining_opt_in_days} days to opt in.`}
        />
      ) : null}
      {pendingAccount &&
      renter.case_status === CaseStatusVariant.PendingOptOut &&
      (renter.remaining_opt_out_days ?? -1) >= 0 ? (
        <NotificationBanner
          icon={<Clock size="24" className="stroke-content-100" />}
          title={`${renter.name} has ${renter.remaining_opt_out_days} days to opt out`}
          text={`${renter.name} has been notified via email and has ${renter.remaining_opt_out_days} days to opt out.`}
        />
      ) : null}
      <div className="flex gap-4 flex-wrap md:flex-nowrap">
        <div className="flex-1 min-w-[350px]">
          <h2 className="mb-2 text-base font-medium text-content-1000">
            Subscription Details
          </h2>
          <div className="min-h-[380px] rounded-lg border border-alpha-100 bg-background-1000 p-6 shadow-xs">
            <InfoRow title={Property} value={renter?.property?.name} />
            <InfoRow title={Unit} value={renter?.unit} />
            <InfoRow title="Address" value={renter?.address} />
            <InfoRow
              title="Move-in date"
              value={
                renter?.move_in_date
                  ? moment(renter.move_in_date).format("MMM D, YYYY")
                  : "--"
              }
            />
            <InfoRow
              title="Move-out date"
              value={
                renter?.move_out_date
                  ? moment(renter.move_out_date).format("MM D, YYYY")
                  : "--"
              }
            />
            <InfoRow
              title="Lease end date"
              value={
                renter?.lease_end_date
                  ? moment(renter.lease_end_date).format("MMM D, YYYY")
                  : "--"
              }
            />
            <InfoRow
              title="Monthly rent"
              value={
                typeof renter?.current_month_payment?.cents === "undefined"
                  ? "--"
                  : formatToCurrency({
                      cents: renter.current_month_payment.cents,
                      currency: "USD"
                    })
              }
            />
          </div>
        </div>
        <div className="flex-1 min-w-[350px]">
          <h2 className="mb-2 text-base font-medium text-content-1000">
            Personal Details
          </h2>
          <div className="min-h-[380px] rounded-lg border border-alpha-100 bg-background-1000 p-6 shadow-xs">
            <InfoRow title="Name" value={renter?.name} />
            <InfoRow
              title="Phone"
              value={renter?.phone ? formatPhone(renter.phone) : "--"}
            />
            <InfoRow title="Email" value={renter?.email} />
            <InfoRow
              title="Date of birth"
              value={
                renter?.dob ? moment(renter.dob).format("MMM D, YYYY") : "--"
              }
            />
            <InfoRow
              title="SSN"
              value={
                renter?.ssn
                  ? renter.ssn.length === 4
                    ? `XXX-XX-${renter.ssn}`
                    : renter.ssn
                  : "--"
              }
            />
          </div>
        </div>
      </div>
      {type.isSDK ? null : (
        <div className="mt-6">
          <h2 className="text-base text-content-1000 font-medium mb-2">
            Rent reporting
          </h2>
          <RentReportingTable
            reportData={renter?.rent_reporting_data as RentReportingData[]}
          />
        </div>
      )}
      <ActivityLogDrawer
        opened={showActivityLog}
        onClose={() => setShowActivityLog(false)}
        acitvityLogs={data || []}
        isLoading={isLoading}
        isError={logsError}
      />
    </Layout>
  );
};
